import { useEffect, useState } from 'react'
import { Check, CircleAlert, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconButton } from './Button'
import { TextField, type FieldLabelProps, type FieldSize, type LabelAccessoryProps } from './Field'
import './copy-field.css'

/**
 * Copies text and resolves true only once the clipboard has it. Where the async Clipboard API is missing or refuses,
 * such as a demo host served over HTTP, it falls back to selecting a hidden textarea and `execCommand('copy')`.
 */
export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // Fall through to the textarea.
  }
  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.opacity = '0'
  document.body.append(textarea)
  const focused = document.activeElement
  try {
    textarea.focus({ preventScroll: true })
    textarea.select()
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    textarea.remove()
    // Selecting the textarea took focus; give it back to whatever had it, such as the button that copied.
    if (focused instanceof HTMLElement && focused !== document.body) focused.focus({ preventScroll: true })
  }
}

/**
 * Copies text that is still on its way, such as a link a request is making. Call it synchronously in the click
 * handler: it hands the clipboard a `ClipboardItem` holding the promise at once, WebKit's pattern for data that arrives
 * later, so Safari keeps the click's permission. Without `ClipboardItem`, or when the clipboard refuses it, it copies
 * the text once it arrives with `copyText`. Resolves false, copying nothing, when the promise rejects.
 */
export async function copyTextFrom(text: Promise<string>): Promise<boolean> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const blob = text.then((value) => new Blob([value], { type: 'text/plain' }))
    // The caller handles a rejected request; this only stops the clipboard's copy of it going unhandled.
    blob.catch(() => {})
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })])
      return true
    } catch {
      // Fall through: the text never came, or the browser refused the item.
    }
  }
  let value: string
  try {
    value = await text
  } catch {
    return false
  }
  return copyText(value)
}

/** "Camera invitation" becomes "Copy camera invitation"; a label that starts with an acronym or name keeps its case. */
const defaultCopyLabel = (label: string) => `Copy ${/^[A-Z][a-z]*(\s|$)/.test(label) ? label[0].toLowerCase() + label.slice(1) : label}`

export type CopyFieldProps = FieldLabelProps & LabelAccessoryProps & {
  /** Visible label. It names the field, and the copy button after it. */
  label: string
  /** The text people copy, such as an invite link, an SRT address or a token. */
  value: string
  /** The copy button's name. Defaults to Copy and the label, such as Copy camera invitation. Set it when the label starts with a name. */
  copyLabel?: string
  /** System monospace with tabular figures. On by default: copied values are usually addresses, keys and codes. */
  mono?: boolean
  size?: FieldSize
  /** Called after each attempt with whether the clipboard took the value, for example to record a successful copy. */
  onCopy?: (copied: boolean) => void
  className?: string
}

type CopyState = 'idle' | 'copied' | 'failed'

/** A read-only value with a copy button. It says Copied only once the clipboard has the value. */
export function CopyField({ label, value, copyLabel, mono = true, size, hideLabel, labelAccessory, onCopy, className }: CopyFieldProps) {
  const [state, setState] = useState<CopyState>('idle')

  useEffect(() => setState('idle'), [value])
  useEffect(() => {
    if (state !== 'copied') return
    const timer = window.setTimeout(() => setState('idle'), 2000)
    return () => window.clearTimeout(timer)
  }, [state])

  async function copy() {
    const copied = await copyText(value)
    setState(copied ? 'copied' : 'failed')
    onCopy?.(copied)
  }

  return (
    <div className={cn('play-copy-field', className)}>
      <div className="play-copy-field__row">
        <TextField
          label={label}
          hideLabel={hideLabel}
          labelAccessory={labelAccessory}
          value={value}
          readOnly
          mono={mono}
          size={size}
          onFocus={(event) => event.currentTarget.select()}
        />
        <IconButton label={copyLabel ?? defaultCopyLabel(label)} variant="ghost" size={size} onClick={() => void copy()}>
          {state === 'copied' ? <Check /> : <Copy />}
        </IconButton>
      </div>
      {/* Always rendered, so the live region exists before its message changes. */}
      <p role="status" className={cn('play-copy-field__status', state === 'failed' && 'play-copy-field__status--failed')}>
        {state === 'copied' && 'Copied'}
        {state === 'failed' && <><CircleAlert aria-hidden="true" />Couldn’t copy. Select the text and copy it.</>}
      </p>
    </div>
  )
}
